// v1.7.0 · CRM · Tareas: pendientes/completadas, vencidas, hoy, próximas; alta y edición
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { api, errMsg, fdate, today, Modal, inputCls, btn, Field, OwnOwner, Empty } from './ui';

export function TaskForm({ meta, task, contactId, onClose, onSaved }) {
  const isNew = !task?.id;
  const [t, setT] = useState({ title: task?.title || '', description: task?.description || '', due_date: task?.due_date || '',
                                assigned_to: task?.assigned_to || meta.me || '' });
  const set = (k) => (e) => setT({ ...t, [k]: e.target.value });
  const save = async () => {
    const body = { ...t, due_date: t.due_date || null, assigned_to: t.assigned_to || null };
    try {
      const r = isNew ? await api.post('/tasks', { ...body, contact_id: contactId || null }) : await api.put(`/tasks/${task.id}`, body);
      toast.success(isNew ? 'Tarea creada' : 'Tarea actualizada'); onSaved(r);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Modal title={isNew ? 'Nueva tarea' : 'Editar tarea'} onClose={onClose}
           footer={<><button className={btn.ghost} onClick={onClose}>Cancelar</button><button className={btn.primary} disabled={!t.title.trim()} onClick={save}>Guardar</button></>}>
      <div className="space-y-3">
        <Field label="Título"><input className={inputCls} value={t.title} onChange={set('title')} autoFocus /></Field>
        <Field label="Descripción"><textarea className={inputCls} rows={3} value={t.description} onChange={set('description')} /></Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Fecha límite"><input type="date" className={inputCls} value={t.due_date} onChange={set('due_date')} /></Field>
          {meta.scope === 'own' ? <OwnOwner meta={meta} label="Asignada a" /> : (   // v1.12.1
          <Field label="Asignada a">
            <select className={inputCls} value={t.assigned_to} onChange={set('assigned_to')}>
              <option value="">Sin asignar</option>{meta.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>
          )}
        </div>
      </div>
    </Modal>
  );
}

export function TaskRow({ t, users, onToggle, onEdit, onDelete, showContact = true }) {
  const overdue = t.status === 'pending' && t.due_date && t.due_date < today();
  return (
    <li className="flex items-start gap-3 py-2.5">
      <input type="checkbox" className="mt-0.5 h-4 w-4" checked={t.status === 'completed'} onChange={() => onToggle(t)} aria-label={`Completar ${t.title}`} />
      <button className="min-w-0 flex-1 text-left" onClick={() => onEdit(t)}>
        <div className={`text-sm font-medium ${t.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{t.title}</div>
        <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-gray-500">
          <span className={overdue ? 'font-semibold text-red-600' : ''}>{t.due_date ? `Vence ${fdate(t.due_date)}` : 'Sin fecha'}</span>
          <span>{users[t.assigned_to] || 'Sin asignar'}</span>
          {showContact && t.contact_id && <Link onClick={(e) => e.stopPropagation()} to={`/crm/contactos/${t.contact_id}`} className="text-[#2F3D8E] hover:underline">{t.contact_name || 'Contacto'}</Link>}
        </div>
      </button>
      <button onClick={() => onDelete(t)} aria-label="Eliminar tarea" className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={14} /></button>
    </li>
  );
}

export default function CrmTasks({ meta }) {
  const [status, setStatus] = useState('pending');
  const [due, setDue] = useState('');
  const [assigned, setAssigned] = useState('');
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const users = useMemo(() => Object.fromEntries(meta.users.map((u) => [u.id, u.name])), [meta.users]);

  const load = useCallback(async () => {
    try { setItems((await api.get('/tasks', { status, due, assigned_to: assigned })).items); } catch (e) { toast.error(errMsg(e)); }
  }, [status, due, assigned]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (t) => {
    try { await api.put(`/tasks/${t.id}`, { status: t.status === 'completed' ? 'pending' : 'completed' }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (t) => {
    if (!window.confirm(`¿Eliminar la tarea «${t.title}»?`)) return;
    try { await api.del(`/tasks/${t.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const pill = (on) => `rounded-full px-3 py-1 text-xs font-semibold ${on ? 'bg-[#2F3D8E] text-white' : 'border border-gray-300 bg-white text-gray-700'}`;

  return (
    <div className="space-y-3" data-testid="crm-tasks">
      <div className="flex flex-wrap items-center gap-2">
        {[['pending', 'Pendientes'], ['completed', 'Completadas'], ['all', 'Todas']].map(([v, l]) => <button key={v} className={pill(status === v)} onClick={() => setStatus(v)}>{l}</button>)}
        <span className="mx-1 h-5 w-px bg-gray-300" />
        {[['', 'Cualquier fecha'], ['overdue', 'Vencidas'], ['today', 'Hoy'], ['upcoming', 'Próximas']].map(([v, l]) => <button key={v} className={pill(due === v)} onClick={() => setDue(v)}>{l}</button>)}
        <select className="ml-auto rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" value={assigned} onChange={(e) => setAssigned(e.target.value)} aria-label="Asignada a">
          <option value="">Todos</option>{meta.me && <option value={meta.me}>Mis tareas</option>}<option value="unassigned">Sin asignar</option>
          {meta.users.filter((u) => u.id !== meta.me).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <button className={btn.primary} onClick={() => setEdit({})}><Plus size={15} /> Tarea</button>
      </div>
      {items.length === 0 ? <Empty title="No hay tareas">Cree tareas aquí o desde la ficha de un contacto.</Empty> : (
        <ul className="divide-y rounded-xl border border-gray-200 bg-white px-4">
          {items.map((t) => <TaskRow key={t.id} t={t} users={users} onToggle={toggle} onEdit={setEdit} onDelete={del} />)}
        </ul>
      )}
      {edit && <TaskForm meta={meta} task={edit.id ? edit : null} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
